import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, ExternalLink, Settings } from "lucide-react";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface CalendarTabProps {
  company: Company | null;
}

const CalendarTab = ({ company }: CalendarTabProps) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Calendrier partagé
          </CardTitle>
          <CardDescription>
            Visualisez les périodes de télétravail de votre équipe dans un calendrier unifié
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center py-12">
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
            
            <h3 className="text-lg font-semibold">Intégration Google Calendar</h3>
            <p className="text-muted-foreground text-sm">
              Cette fonctionnalité sera bientôt disponible. Elle permettra de :
            </p>
            
            <div className="text-left space-y-2 max-w-sm mx-auto">
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>Synchroniser avec Google Calendar</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>Voir les périodes de télétravail approuvées</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>Planifier les ressources par pays</span>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                <span>Gérer les conflits de planning</span>
              </div>
            </div>

            <div className="pt-4 space-y-2">
              <Button disabled className="w-full">
                <Settings className="h-4 w-4 mr-2" />
                Configurer Google Calendar
              </Button>
              <p className="text-xs text-muted-foreground">
                Fonctionnalité en développement
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview of what the calendar will look like */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Aperçu du calendrier</CardTitle>
          <CardDescription>
            Voici à quoi ressemblera le calendrier une fois intégré
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-4 bg-muted/30">
            <div className="grid grid-cols-7 gap-2 mb-4">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day) => (
                <div key={day} className="text-center text-sm font-medium p-2">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 35 }, (_, i) => {
                const day = i + 1;
                const hasRemoteWork = [5, 12, 18, 25].includes(day);
                
                return (
                  <div
                    key={i}
                    className={`aspect-square border rounded p-1 text-xs ${
                      hasRemoteWork 
                        ? 'bg-blue-100 border-blue-300 text-blue-800' 
                        : 'bg-background'
                    }`}
                  >
                    {day <= 31 ? (
                      <div>
                        <div>{day}</div>
                        {hasRemoteWork && (
                          <div className="mt-1 text-[10px] truncate">
                            2 télétravail
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
                  <span>Jours avec télétravail</span>
                </div>
              </div>
              <Button variant="ghost" size="sm" disabled>
                <ExternalLink className="h-3 w-3 mr-1" />
                Ouvrir dans Google Calendar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarTab;